/**
 * 4★+ → Google review handoff (Rate Your Tutor modal on the course page).
 *
 * Google has no API for creating a review on a user's behalf — the Google
 * Business Profile API only reads reviews and posts owner replies. A review can
 * only be authored by a signed-in Google account through Google's own UI, so the
 * closest thing to a real-time publish is a one-click handoff: copy the
 * student's own feedback to their clipboard and open the correct Google review
 * page so they paste and post it themselves (live within seconds).
 *
 * Only ratings at or above {@link GOOGLE_REVIEW_MIN_RATING} are routed to
 * Google. Every lower rating follows the existing flow unchanged.
 */

/**
 * PostHog multivariate feature flag.
 *
 * Create in PostHog: Feature flag → Multivariate, key must match this constant.
 * Variants:
 * - `control` — existing behaviour (submit → toast → close). No Google step.
 * - `test`    — 4★ and 5★ submissions get the Google review handoff step.
 */
export const POSTHOG_FLAG_GOOGLE_REVIEW_HANDOFF = 'google_review_handoff';

/** Variant key that turns the handoff on. */
export const GOOGLE_REVIEW_HANDOFF_TEST_VARIANT = 'test';

/** Lowest rating routed to Google. Anything below this keeps the existing flow. */
export const GOOGLE_REVIEW_MIN_RATING = 4;

/** Tutopiya UAE Google Business Profile. */
export const GOOGLE_REVIEW_URL_UAE = 'https://g.page/r/Cb3ZZoF6aJWcEBM/review';

/** Tutopiya Singapore Google Business Profile — default for every non-UAE student. */
export const GOOGLE_REVIEW_URL_SG = 'https://g.page/r/CfoNUY7loY80EBM/review';

/** Business profile the student was sent to (analytics + copy). */
export type TGoogleReviewProfile = 'UAE' | 'SG';

export const GOOGLE_REVIEW_PROFILES: Record<string, TGoogleReviewProfile> = {
  UAE: 'UAE',
  SG: 'SG',
};

/** ISO 3166-1 alpha-2 code that routes to the UAE profile. */
export const UAE_COUNTRY_CODE = 'AE';

/**
 * IANA timezones that mean the student is in the UAE. `IUser.country` stores a
 * timezone (e.g. `Asia/Dubai`), not an ISO code, so this is the primary signal.
 */
export const UAE_TIMEZONES = ['Asia/Dubai'];

/** UAE dial code, matched against the digits of the student's phone number. */
export const UAE_DIAL_CODE = '971';
